import { formatCurrency } from 'common/FormatUtilities';
import { DateTime } from 'luxon';
import React from 'react';
import { Link } from 'react-router-dom';
import { ConsumerGroupOrderFieldsFragment, OrderStatus, OrderType } from 'src/gqlReactTypings.generated.d';
import { TabNames } from 'src/pages/consumer/account/orders/utilities';
import { ChipColor } from 'src/shared/design-system/Chip';
import { ChevronRightV2 } from 'src/shared/design-system/Icon';
import { VerticallyCenteredText } from 'src/shared/design-system/VerticallyCenteredText';
import { Routes } from 'src/shared/Routes';
import { getLocalTimezone } from 'src/shared/utils/DateTimeUtilities';
import { getIdSluggedPath } from 'src/shared/utils/RouteUtilities';
import { match } from 'ts-pattern';
import { OrdersCardHeader, OrdersCardHeaderChip, OrdersCardPriceText, RecurringOrderIcon } from './styles';

interface ConsumerOrdersCardHeaderProps {
  groupOrder: ConsumerGroupOrderFieldsFragment;
  activeTab: TabNames;
}

export const ConsumerOrdersCardHeader: React.FC<ConsumerOrdersCardHeaderProps> = ({ groupOrder, activeTab }) => {
  const { deliveryDate, orderType, id, orderTotals, isCancelled, isFullyRefunded, isPartiallyRefunded } = groupOrder;

  const orderDateText = DateTime.fromJSDate(new Date(deliveryDate), { zone: getLocalTimezone() }).toLocaleString(
    DateTime.DATE_MED_WITH_WEEKDAY
  );

  const chipProps = match({
    isDelivered: activeTab === TabNames.PREVIOUS,
    isCancelled,
    isRefunded: isFullyRefunded || isPartiallyRefunded,
    orderType,
  })
    .with({ orderType: OrderType.Recurring, isCancelled: true }, () => ({
      text: `Skipped for ${orderDateText}`,
      color: ChipColor.YELLOW,
    }))
    .with({ isCancelled: true }, () => ({
      text: `Cancelled on ${orderDateText}`,
      color: ChipColor.YELLOW,
    }))
    .with({ isRefunded: true }, () => ({
      text: `Refunded on ${orderDateText}`,
      color: ChipColor.YELLOW,
    }))
    .with({ isDelivered: true }, () => ({ text: `Order from ${orderDateText}`, color: ChipColor.EGGPLANT }))
    .with({ orderType: OrderType.Recurring }, () => ({
      text: `Next Order: ${orderDateText}`,
      color: ChipColor.RED,
      icon: <RecurringOrderIcon />,
    }))
    .otherwise(() => ({ text: `Order for ${orderDateText}`, color: ChipColor.EGGPLANT }));

  const isMealPlanHeldOrder = groupOrder.orders.some(({ orderStatus }) => orderStatus === OrderStatus.Held);

  const receiptPath = isMealPlanHeldOrder
    ? Routes.CONSUMER_MEAL_PLANS_UPCOMING_ORDER
    : getIdSluggedPath(Routes.CONSUMER_ORDER_RECEIPT, id);

  return (
    <Link to={receiptPath} data-cy='order-receipt-link'>
      <OrdersCardHeader>
        <OrdersCardHeaderChip {...chipProps} />
        <OrdersCardPriceText>
          <VerticallyCenteredText>
            {isMealPlanHeldOrder ? 'Manage' : formatCurrency(orderTotals.grandTotal)}
          </VerticallyCenteredText>
          <ChevronRightV2 />
        </OrdersCardPriceText>
      </OrdersCardHeader>
    </Link>
  );
};
